/**
 * FilterPanel - Global filters section
 */

import React from "react";
import { Grid, Box, Typography, Button, ButtonGroup } from "@material-ui/core";
import { Clear as ClearIcon, Info as InfoIcon } from "@material-ui/icons";
import { FilterAutocomplete } from "../molecules";
// NOTE: AggSystemWellsAutocomplete allows searching by well name/number within agg systems
// Commented out for now but may be re-enabled in the future
// import { AggSystemWellsAutocomplete } from "../molecules";
import { FiltersContainer, ButtonContainer } from "../styled";
import { COLUMN_MODES } from "../../constants";

/**
 * Global filter panel component
 *
 * @param {Object} props
 * @param {Object} props.filterOptions - Available filter options
 * @param {Array} props.selectedYears - Selected years
 * @param {Function} props.setSelectedYears - Set selected years
 * @param {Array} props.selectedAquifers - Selected aquifers
 * @param {Function} props.setSelectedAquifers - Set selected aquifers
 * @param {Array} props.selectedAggSystems - Selected agg systems
 * @param {Function} props.setSelectedAggSystems - Set selected agg systems
 * @param {Array} props.selectedWells - Selected wells
 * @param {Function} props.setSelectedWells - Set selected wells
 * @param {boolean} props.isWellFilterActive - Whether well filter is active
 * @param {number} props.recordCount - Number of filtered records
 * @param {string} props.columnMode - Current column mode
 * @param {Function} props.setColumnMode - Set column mode
 * @param {Function} props.onResetFilters - Reset filters handler
 */
const FilterPanel = ({
  filterOptions,
  selectedYears,
  setSelectedYears,
  selectedAquifers,
  setSelectedAquifers,
  selectedAggSystems,
  setSelectedAggSystems,
  selectedWells,
  setSelectedWells,
  isWellFilterActive,
  recordCount,
  columnMode,
  setColumnMode,
  onResetFilters,
}) => {
  return (
    <FiltersContainer>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FilterAutocomplete
            label="Years"
            options={filterOptions.years}
            selectedValues={selectedYears}
            onChange={setSelectedYears}
            getOptionLabel={(option) => String(option)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FilterAutocomplete
            label="Aquifers"
            options={filterOptions.aquifers}
            selectedValues={selectedAquifers}
            onChange={setSelectedAquifers}
            getOptionLabel={(option) => option.name || "Unknown"}
            getOptionSelected={(option, value) => option.ndx === value.ndx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* NOTE: AggSystemWellsAutocomplete allows searching by well name/number within agg systems
          <AggSystemWellsAutocomplete
            aggSystems={filterOptions.aggSystems}
            wells={filterOptions.wells}
            selectedAggSystems={selectedAggSystems}
            onChange={setSelectedAggSystems}
          />
          */}
          <FilterAutocomplete
            label="Agg Systems"
            options={filterOptions.aggSystems}
            selectedValues={selectedAggSystems}
            onChange={setSelectedAggSystems}
            getOptionLabel={(option) => option.name || "Unknown"}
            getOptionSelected={(option, value) => option.ndx === value.ndx}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FilterAutocomplete
            label="Wells"
            options={filterOptions.wells}
            selectedValues={selectedWells}
            onChange={setSelectedWells}
            getOptionLabel={(option) =>
              option.number
                ? `${option.number}${option.name ? ` - ${option.name}` : ""}`
                : option.name || "Unknown"
            }
            getOptionSelected={(option, value) => option.ndx === value.ndx}
          />
        </Grid>
      </Grid>
      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {recordCount.toLocaleString()} records
          </Typography>
          {isWellFilterActive && (
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 4,
                color: "#1976d2",
                backgroundColor: "#e3f2fd",
                padding: "2px 8px",
                borderRadius: 4,
              }}
            >
              <InfoIcon style={{ fontSize: 16 }} />
              <Typography variant="caption">
                Well filter active - viewing individual well contributions
              </Typography>
            </Box>
          )}
        </Box>
        <ButtonContainer>
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={
                columnMode === COLUMN_MODES.compact ? "contained" : "outlined"
              }
              color={
                columnMode === COLUMN_MODES.compact ? "primary" : "default"
              }
              onClick={() => setColumnMode(COLUMN_MODES.compact)}
            >
              Compact
            </Button>
            <Button
              variant={
                columnMode === COLUMN_MODES.full ? "contained" : "outlined"
              }
              color={columnMode === COLUMN_MODES.full ? "primary" : "default"}
              onClick={() => setColumnMode(COLUMN_MODES.full)}
            >
              Full
            </Button>
          </ButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onResetFilters}
          >
            Reset to Default
          </Button>
        </ButtonContainer>
      </Box>
    </FiltersContainer>
  );
};

export default FilterPanel;
